import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { authService } from "../services/api";

const Navigation = ({ isCollapsed = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [useBillingInsights, setUseBillingInsights] = useState(false);
  const [accountInfo, setAccountInfo] = useState(null);
  const [authMethod, setAuthMethod] = useState("oauth");

  // Always start with billing insights unchecked (default state)
  // Note: We still save the preference when user toggles it, but always default to unchecked

  // Fetch account information and auth method on mount
  useEffect(() => {
    const fetchAuthMethod = async () => {
      try {
        const { authMethod: method } = await authService.checkAuthStatus();
        setAuthMethod(method || "oauth");
        return method || "oauth";
      } catch (error) {
        console.error("Error fetching auth method:", error);
        return "oauth";
      }
    };

    const fetchAccountInfo = async () => {
      try {
        // Check auth method first - org tokens cannot access account endpoint
        const method = await fetchAuthMethod();
        if (method === "org-token") {
          // Org tokens don't have access to account info
          return;
        }

        const data = await authService.getAccountInfo();
        setAccountInfo(data.account);
      } catch (error) {
        // Handle 403 specifically for org tokens
        if (error.response?.status === 403) {
          console.log("Account info not available for organization tokens");
        } else {
          console.error("Error fetching account info:", error);
        }
      }
    };

    fetchAccountInfo();
  }, []);

  // Load billing insights state from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem("useBillingInsights");
    if (saved === "true") {
      setUseBillingInsights(true);
    }
  }, []);

  const handleLogout = async () => {
    try {
      await authService.logout();
      navigate("/login");
    } catch (error) {
      console.error("Logout failed:", error);
      // Still navigate to login even if logout fails
      navigate("/login");
    }
  };

  return (
    <aside
      className={`bg-white border-r border-cf-gray-200 flex flex-col transition-all duration-300 ${
        isCollapsed ? "w-0 md:w-16" : "w-64"
      } ${isCollapsed ? "overflow-hidden" : ""}`}
    >
      {/* Team Information */}
      {accountInfo?.team && !isCollapsed && (
        <div className="px-3 py-3 border-b border-cf-gray-200 bg-cf-gray-50">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 text-cf-gray-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
              <span className="text-xs font-semibold text-cf-gray-500 uppercase tracking-wider">
                Team
              </span>
            </div>

            <div className="inline-flex items-center px-2.5 py-1.5 rounded-cf bg-do-blue-50 border border-do-blue-200">
              <span
                className="text-sm font-medium text-do-blue-700 truncate"
                title={accountInfo.team.name}
              >
                {accountInfo.team.name}
              </span>
            </div>

            <div className="flex items-start gap-1.5">
              <span className="text-xs text-cf-gray-500 mt-0.5 flex-shrink-0">
                UUID:
              </span>
              <code
                className="text-xs text-cf-gray-600 font-mono break-all leading-relaxed"
                title={accountInfo.team.uuid}
              >
                {accountInfo.team.uuid}
              </code>
            </div>
          </div>
        </div>
      )}

      {/* Navigation Links */}
      <nav
        className={`flex-1 py-4 space-y-1 ${isCollapsed ? "hidden md:flex md:flex-col md:px-2" : "px-3"}`}
      >
        <Link
          to="/dashboard"
          onClick={() => {
            // Disable billing insights when clicking Invoice Analysis
            if (useBillingInsights) {
              setUseBillingInsights(false);
              localStorage.setItem("useBillingInsights", "false");
              window.dispatchEvent(new Event("billingModeChanged"));
            }
          }}
          title={isCollapsed ? "Invoice Analysis" : ""}
          className={`flex items-center rounded-cf text-sm font-medium transition-colors ${
            isCollapsed ? "justify-center p-2" : "px-3 py-2"
          } ${
            location.pathname === "/dashboard" && !useBillingInsights
              ? "bg-do-blue-50 text-do-blue-700 border border-do-blue-200"
              : "text-cf-gray-700 hover:bg-cf-gray-50 hover:text-cf-gray-900"
          }`}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={`h-5 w-5 ${isCollapsed ? "" : "mr-3"}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
            />
          </svg>
          {!isCollapsed && "Invoice Analysis"}
        </Link>

        <Link
          to="/dashboard"
          onClick={(e) => {
            // Enable billing insights when clicking Billing Insights
            if (!useBillingInsights) {
              setUseBillingInsights(true);
              localStorage.setItem("useBillingInsights", "true");
              window.dispatchEvent(new Event("billingModeChanged"));
            }
          }}
          title={isCollapsed ? "Billing Insights" : ""}
          className={`flex items-center rounded-cf text-sm font-medium transition-colors ${
            isCollapsed ? "justify-center p-2" : "px-3 py-2"
          } ${
            location.pathname === "/dashboard" && useBillingInsights
              ? "bg-do-blue-50 text-do-blue-700 border border-do-blue-200"
              : "text-cf-gray-700 hover:bg-cf-gray-50 hover:text-cf-gray-900"
          }`}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={`h-5 w-5 ${isCollapsed ? "" : "mr-3"}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
            />
          </svg>
          {!isCollapsed && "Billing Insights"}
        </Link>

        <Link
          to="/serverless-inference"
          title={isCollapsed ? "GenAI Inference" : ""}
          className={`flex items-center rounded-cf text-sm font-medium transition-colors ${
            isCollapsed ? "justify-center p-2" : "px-3 py-2"
          } ${
            location.pathname === "/serverless-inference"
              ? "bg-do-blue-50 text-do-blue-700 border border-do-blue-200"
              : "text-cf-gray-700 hover:bg-cf-gray-50 hover:text-cf-gray-900"
          }`}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={`h-5 w-5 ${isCollapsed ? "" : "mr-3"}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
            />
          </svg>
          {!isCollapsed && "GenAI Inference"}
        </Link>

        {authMethod === "oauth" && (
          <>
            <Link
              to="/droplets"
              title={isCollapsed ? "Droplet Costs" : ""}
              className={`flex items-center rounded-cf text-sm font-medium transition-colors ${
                isCollapsed ? "justify-center p-2" : "px-3 py-2"
              } ${
                location.pathname === "/droplets"
                  ? "bg-do-blue-50 text-do-blue-700 border border-do-blue-200"
                  : "text-cf-gray-700 hover:bg-cf-gray-50 hover:text-cf-gray-900"
              }`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className={`h-5 w-5 ${isCollapsed ? "" : "mr-3"}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01"
                />
              </svg>
              {!isCollapsed && "Droplet Costs"}
            </Link>

            <Link
              to="/bandwidth"
              title={isCollapsed ? "Bandwidth" : ""}
              className={`flex items-center rounded-cf text-sm font-medium transition-colors ${
                isCollapsed ? "justify-center p-2" : "px-3 py-2"
              } ${
                location.pathname === "/bandwidth"
                  ? "bg-do-blue-50 text-do-blue-700 border border-do-blue-200"
                  : "text-cf-gray-700 hover:bg-cf-gray-50 hover:text-cf-gray-900"
              }`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className={`h-5 w-5 ${isCollapsed ? "" : "mr-3"}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
                />
              </svg>
              {!isCollapsed && "Bandwidth"}
            </Link>
          </>
        )}
      </nav>

      {/* Footer Section */}
      <div
        className={`border-t border-cf-gray-200 ${isCollapsed ? "hidden md:block md:p-2" : "p-4"}`}
      >
        <button
          onClick={handleLogout}
          title={isCollapsed ? "Logout" : ""}
          className={`w-full rounded-cf text-sm font-medium text-cf-gray-700 border border-cf-gray-300 hover:bg-cf-gray-50 transition-colors flex items-center ${
            isCollapsed ? "justify-center p-2" : "px-4 py-2 justify-center"
          }`}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={`h-4 w-4 ${isCollapsed ? "" : "mr-2"}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
            />
          </svg>
          {!isCollapsed && "Logout"}
        </button>

        {!isCollapsed && (
          <div className="mt-4 text-center text-xs text-cf-gray-500">
            <p>
              Created by{" "}
              <a
                href="https://www.jackpearce.co.uk/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-do-blue hover:underline"
              >
                Jack Pearce
              </a>
            </p>
            <div className="mt-2">
              <a
                href="https://github.com/digitalocean-labs/cost-explorer"
                target="_blank"
                rel="noopener noreferrer"
                className="text-do-blue hover:underline"
              >
                GitHub
              </a>
            </div>
          </div>
        )}
      </div>
    </aside>
  );
};

export default Navigation;
